import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, distinctUntilChanged, pipe, switchMap, tap } from 'rxjs';
import { VaultPositionApi } from '../api/vault-position.api';
import type {
  NavHistoryItem,
  VaultActivityItem,
  VaultLockTier,
  VaultOverview,
  VaultPosition,
  VaultTrade,
} from './vault-position.schema';

export const VaultPositionStore = signalStore(
  { providedIn: 'root' },
  withState({
    overview: null as VaultOverview | null,
    position: null as VaultPosition | null,
    lockTiers: [] as VaultLockTier[],
    lockTiersError: null as string | null,
    navHistory: [] as NavHistoryItem[],
    trades: [] as VaultTrade[],
    tradesCount: 0,
    activity: [] as VaultActivityItem[],
    activityCount: 0,
  }),
  withMethods((store, api = inject(VaultPositionApi)) => {
    const loadOverview = rxMethod<void>(
      pipe(
        switchMap(() =>
          api.overview$().pipe(
            tap(overview => patchState(store, { overview })),
            catchError(() => EMPTY),
          ),
        ),
      ),
    );

    const loadPosition = rxMethod<void>(
      pipe(
        switchMap(() =>
          api.myPosition$().pipe(
            tap(position => patchState(store, { position })),
            catchError(() => EMPTY),
          ),
        ),
      ),
    );

    const loadLockTiers = rxMethod<void>(
      pipe(
        switchMap(() =>
          api.lockTiers$().pipe(
            tap(lockTiers => patchState(store, { lockTiers, lockTiersError: null })),
            catchError((err: unknown) => {
              const message =
                err instanceof Error && err.message ? err.message : 'invest.actions.lockTiersError';
              patchState(store, { lockTiersError: message });
              return EMPTY;
            }),
          ),
        ),
      ),
    );

    const loadNavHistory = rxMethod<string>(
      pipe(
        distinctUntilChanged(),
        switchMap(period =>
          api.navHistory$(period).pipe(
            tap(navHistory => patchState(store, { navHistory })),
            catchError(() => EMPTY),
          ),
        ),
      ),
    );

    const loadTrades = rxMethod<number>(
      pipe(
        switchMap(offset =>
          api.tradesList$(20, offset).pipe(
            tap(({ data, count }) => patchState(store, { trades: data, tradesCount: count })),
            catchError(() => EMPTY),
          ),
        ),
      ),
    );

    const loadActivity = rxMethod<number>(
      pipe(
        switchMap(offset =>
          api.myActivity$(20, offset).pipe(
            tap(({ data, count }) => patchState(store, { activity: data, activityCount: count })),
            catchError(() => EMPTY),
          ),
        ),
      ),
    );

    const reset = (): void => {
      patchState(store, { position: null, activity: [], activityCount: 0 });
    };

    return {
      loadOverview,
      loadPosition,
      loadLockTiers,
      loadNavHistory,
      loadTrades,
      loadActivity,
      reset,
    };
  }),
);
